import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PreRegistrationStatusBadge } from './PreRegistrationStatusBadge';
import { preRegistrationRoutes } from '../utils/routes.utils';
import { NO_LOCALITY_LABEL, formatOptionalCount, formatTimestamp } from '../utils/format.utils';
import type { PreRegisteredAccommodation } from '../types/pre-registration.types';

interface PreRegisteredAccommodationsTableProps {
  items: PreRegisteredAccommodation[];
}

export const PreRegisteredAccommodationsTable: React.FC<PreRegisteredAccommodationsTableProps> = ({
  items,
}) => {
  const navigate = useNavigate();

  return (
    <Table className="border border-gray-200">
      <TableHeader className="bg-gray-100">
        <TableRow>
          <TableHead>Denominación</TableHead>
          <TableHead>Municipio</TableHead>
          <TableHead>Localidad</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Plazas</TableHead>
          <TableHead className="text-right">Habitaciones</TableHead>
          <TableHead>Fecha de estado</TableHead>
          <TableHead className="text-right">Acciones</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow
            key={item.id}
            className="cursor-pointer"
            onClick={() => navigate(preRegistrationRoutes.accommodation(item.municipalListId, item.id))}
          >
            <TableCell className="font-medium">{item.establishmentName}</TableCell>
            <TableCell>{item.municipalList.municipality.name}</TableCell>
            <TableCell className={item.locality ? undefined : 'text-gray-500'}>
              {item.locality?.name ?? NO_LOCALITY_LABEL}
            </TableCell>
            <TableCell>
              <PreRegistrationStatusBadge status={item.status} />
            </TableCell>
            <TableCell className="text-right">{formatOptionalCount(item.places)}</TableCell>
            <TableCell className="text-right">{formatOptionalCount(item.rooms)}</TableCell>
            <TableCell>{formatTimestamp(item.statusDate)}</TableCell>
            {/* Buttons must not trigger the row navigation */}
            <TableCell className="text-right" onClick={(event) => event.stopPropagation()}>
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="icon" asChild title="Ver alojamiento">
                  <Link to={preRegistrationRoutes.accommodation(item.municipalListId, item.id)}>
                    <Eye className="h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="ghost" size="icon" asChild title="Ver listado municipal">
                  <Link to={preRegistrationRoutes.municipalList(item.municipalListId)}>
                    <FileText className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
